import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Link } from 'react-router-dom';

import { safeHref } from '@/lib/utils/url';
import { WidgetFrame } from '@/pages/home/widgets/WidgetFrame';
import { useMyOpenChatRooms, type PmisOpenChatRoom } from '@/pages/sites/pmis/queries';

/**
 * 내 현장 대화방 — 내 소속 현장의 카카오톡 오픈채팅방 입구(DOC-26 §5).
 *
 * <b>이 위젯이 이 기능의 값을 대부분 만든다.</b> 오픈채팅 주소는 지금 누군가의 카톡 대화 속에
 * 묻혀 있어서, 신규 발령자는 링크를 물어볼 사람부터 찾아야 한다. 발령이 곧 목록이 되는 자리는
 * 카톡이 아니라 여기다 — 카톡에게 현장은 방 제목 문자열일 뿐이다.
 *
 * <h2>왜 줄이 아니라 카드인가</h2>
 * 목록 한 줄로 두면 방 이름 하나만 남고 <b>현장·용도가 뒤로 밀려 잘린다</b> — 이 위젯에서
 * 사람이 실제로 고르는 기준이 그 둘인데도 그렇다. 같은 현장에 "공무", "안전", "협력사" 방이
 * 나란히 있으면 이름만으로는 어느 방인지 가려지지 않는다.
 *
 * <h2>왜 가로로 넘기는가</h2>
 * 카드를 접어 쌓으면 방이 많은 사람의 홈에서 이 위젯 하나가 화면을 다 먹는다. 방 개수는
 * 사용자가 정하는 값이 아니라 <b>발령이 정하는 값</b>이라, 개수에 따라 홈 배치가 달라지면
 * 사용자는 자기가 고치지 않은 화면이 바뀌는 것을 본다. 그래서 카드 한 장 높이로 <b>높이를
 * 고정</b>하고 나머지는 옆으로 흘린다 — 위젯 크기는 사용자가 위젯 관리에서 정한 그대로 남는다.
 *
 * 넘기는 방법은 세 가지다: 손가락(네이티브 스크롤), 마우스로 끌기, 머리의 ‹ › 버튼.
 * 마우스 끌기를 따로 붙인 이유는 데스크톱에는 손가락 스크롤이 없어서 가로 목록이 "넘길 수
 * 있는 것"으로 보이지 않기 때문이다.
 *
 * 카드를 누르면 <b>카카오톡으로 나간다</b>(사외 링크 규칙: `target=_blank` + `rel=noopener noreferrer`).
 *
 * 본사·관리자에게는 전 현장 방이 온다(서버 판정). 자기 소속으로 좁히면 늘 비어 있게 되기 때문이다.
 */

/**
 * 띠에 실을 카드 수. 가로로 흘리므로 높이 때문에 자를 이유는 없지만, 방이 수십 개인
 * 본사·관리자에게 홈이 끝없는 띠가 되는 것은 목록 화면이 할 일이다. 나머지는 마지막 칸의
 * "+N" 이 받는다 — 세어 보이는 편이 잘라 놓고 아무 말도 하지 않는 것보다 낫다.
 */
const MAX_CARDS = 12;

export function SiteChatRoomsWidget() {
  const rooms = useMyOpenChatRooms();
  const list = rooms.data ?? [];
  const shown = list.slice(0, MAX_CARDS);
  const rest = list.length - shown.length;

  const strip = useDragScroll();

  return (
    <WidgetFrame
      title="내 현장 대화방"
      icon="message"
      to="/sites/chats"
      pending={rooms.isPending}
      error={rooms.error}
      empty={list.length === 0}
      emptyText="내 소속 현장에 등록된 대화방이 없습니다. 현장 조회 > 대화방에서 등록합니다."
      headerRight={
        strip.scrollable ? (
          <span className="flex items-center gap-1">
            <button
              type="button"
              className="chat-room-nav"
              aria-label="이전 대화방"
              disabled={!strip.canScrollLeft}
              onClick={() => strip.scrollByPage(-1)}
            >
              ‹
            </button>
            <button
              type="button"
              className="chat-room-nav"
              aria-label="다음 대화방"
              disabled={!strip.canScrollRight}
              onClick={() => strip.scrollByPage(1)}
            >
              ›
            </button>
          </span>
        ) : undefined
      }
    >
      <div
        ref={strip.ref}
        className={`chat-room-strip${strip.dragging ? ' is-dragging' : ''}`}
        onPointerDown={strip.onPointerDown}
        onClickCapture={strip.onClickCapture}
        // 끄는 도중 브라우저가 링크를 "끌어다 놓기" 로 잡아 고스트 이미지를 띄우면 스크롤이 끊긴다.
        onDragStart={(event) => event.preventDefault()}
        role="list"
      >
        {shown.map((room) => (
          <ChatRoomCard key={room.id} room={room} />
        ))}
        {rest > 0 && (
          <Link to="/sites/chats" className="chat-room-card chat-room-card-more" role="listitem">
            +{rest}개 더 보기
          </Link>
        )}
      </div>
      <p className="text-ui-2xs" style={{ color: 'var(--text-muted)', marginTop: '4px' }}>
        카카오톡으로 연결됩니다. 대화 내용은 저장되지 않습니다.
      </p>
    </WidgetFrame>
  );
}

function ChatRoomCard({ room }: { room: PmisOpenChatRoom }) {
  return (
    <a
      href={safeHref(room.inviteUrl)}
      target="_blank"
      rel="noopener noreferrer"
      className="chat-room-card"
      title={`${room.siteName} · ${room.title}`}
      role="listitem"
    >
      <div className="chat-room-card-head">
        <span className="chat-room-card-avatar" aria-hidden>
          {siteInitial(room.siteName)}
        </span>
        <span className="chat-room-card-site">{room.siteName}</span>
      </div>
      <span className="chat-room-card-title">{room.title}</span>
      <div className="chat-room-card-foot">
        {room.purpose ? (
          <span className="chat-room-card-tag">{room.purpose}</span>
        ) : (
          <span className="chat-room-card-manager">방장 {room.managerName || '미지정'}</span>
        )}
        <span className="chat-room-card-open">열기 ›</span>
      </div>
    </a>
  );
}

/**
 * 가로 띠를 <b>마우스로 끌어서</b> 넘기게 한다.
 *
 * 터치는 브라우저가 이미 해 준다(그래서 마우스만 가로챈다 — 손가락까지 잡으면 관성 스크롤과
 * 당겨서 새로고침 같은 네이티브 동작을 우리가 다시 만들어야 한다).
 *
 * <h3>끌었는데 카톡이 열리면 안 된다</h3>
 * 카드가 통째로 링크라, 끌고 손을 떼면 그대로 클릭이 된다. 그래서 <b>몇 px 넘게 움직였는지</b>를
 * 기억해 두었다가 뒤이어 오는 click 을 캡처 단계에서 막는다. 문턱(4px)을 두는 이유는 사람이
 * 누를 때 손이 1~2px 씩 흔들리기 때문이다 — 0 으로 두면 평범한 클릭이 먹히지 않는다.
 */
function useDragScroll() {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef({ pointerId: -1, startX: 0, startLeft: 0, moved: false });
  const [dragging, setDragging] = useState(false);
  const [edges, setEdges] = useState({ scrollable: false, left: false, right: false });

  const sync = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // 1px 여유. 브라우저가 소수점 스크롤 위치를 주므로 정확히 같아지는 순간이 오지 않는다.
    const max = el.scrollWidth - el.clientWidth;
    setEdges({ scrollable: max > 1, left: el.scrollLeft > 1, right: el.scrollLeft < max - 1 });
  }, []);

  // 방 목록이 늦게 도착하면 띠의 폭도 그때 정해진다. 스크롤·크기 변화를 모두 듣는다.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    sync();
    el.addEventListener('scroll', sync, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(sync);
    observer?.observe(el);
    return () => {
      el.removeEventListener('scroll', sync);
      observer?.disconnect();
    };
  }, [sync]);

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const el = ref.current;
    if (!el) return;
    drag.current = { pointerId: event.pointerId, startX: event.clientX, startLeft: el.scrollLeft, moved: false };

    const onMove = (move: globalThis.PointerEvent) => {
      if (move.pointerId !== drag.current.pointerId) return;
      const dx = move.clientX - drag.current.startX;
      if (!drag.current.moved) {
        if (Math.abs(dx) < 4) return;
        drag.current.moved = true;
        setDragging(true);
      }
      el.scrollLeft = drag.current.startLeft - dx;
    };

    const onUp = (up: globalThis.PointerEvent) => {
      if (up.pointerId !== drag.current.pointerId) return;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      setDragging(false);
      if (!drag.current.moved) return;
      // 끌기로 끝난 동작이면 뒤따르는 click 한 번을 `onClickCapture` 가 삼킨다. pointerup → click 은
      // 같은 작업 안에서 이어지므로, 타이머 0 은 그 click 뒤에 돈다 — 띠 밖에서 손을 떼 click 이
      // 아예 오지 않은 경우에도 표식이 남아 다음 클릭을 잡아먹지 않게 여기서 지운다.
      window.setTimeout(() => {
        drag.current.moved = false;
      }, 0);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, []);

  const scrollByPage = useCallback((direction: 1 | -1) => {
    const el = ref.current;
    if (!el) return;
    // 한 번에 보이는 폭의 8할씩. 딱 한 폭을 넘기면 경계의 카드가 통째로 사라져 어디까지 봤는지 잃는다.
    el.scrollBy({ left: direction * Math.max(120, el.clientWidth * 0.8), behavior: 'smooth' });
  }, []);

  const onClickCapture = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    if (!drag.current.moved) return;
    drag.current.moved = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return {
    ref,
    dragging,
    onPointerDown,
    onClickCapture,
    scrollByPage,
    scrollable: edges.scrollable,
    canScrollLeft: edges.left,
    canScrollRight: edges.right,
  };
}

/**
 * 현장 이름 첫 글자. `[...name]` 으로 쪼개는 이유는 이모지·확장 한자처럼 UTF-16 두 칸을 쓰는
 * 글자에서 `name[0]` 이 반쪽만 잘라 깨진 문자를 그리기 때문이다.
 */
function siteInitial(siteName: string): string {
  return [...siteName.trim()][0] ?? '·';
}
